/**
 * NotificationBell — bell button with unread badge (WI-56).
 *
 * Renders only when authenticated (caller must gate on useAuth().user).
 * Uses BellIcon from @heroicons/react/24/outline at 20×20px.
 *
 * Badge display rules (per design):
 *   0       → no badge
 *   1–9     → digit as-is
 *   10–99   → "9+"
 *   100+    → "99+"
 *
 * aria-label uses the exact count (e.g. "Notifications — 14 unread"),
 * not the display-capped label. The badge itself is aria-hidden.
 *
 * Accessibility:
 *   - aria-expanded reflects dropdown open state
 *   - aria-haspopup="dialog"
 *   - Escape key handled here: closes dropdown + returns focus to bell
 */

import React, { useRef, useEffect } from 'react';
import { BellIcon } from '@heroicons/react/24/outline';

interface NotificationBellProps {
  unreadCount: number;
  isOpen: boolean;
  onClick: () => void;
  onClose: () => void;
}

/** Compute the display label for the badge. */
export function badgeLabel(count: number): string {
  if (count <= 9) return String(count);
  if (count <= 99) return '9+';
  return '99+';
}

export function NotificationBell({
  unreadCount,
  isOpen,
  onClick,
  onClose,
}: NotificationBellProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Escape key: close dropdown and return focus to bell button.
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const ariaLabel =
    unreadCount > 0 ? `Notifications — ${unreadCount} unread` : 'Notifications';

  return (
    <button
      ref={buttonRef}
      type="button"
      aria-label={ariaLabel}
      aria-expanded={isOpen}
      aria-haspopup="dialog"
      onClick={onClick}
      className="relative flex items-center justify-center w-11 h-11 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
      style={{ color: 'var(--ors-ink-2)' }}
    >
      <BellIcon width={20} height={20} aria-hidden="true" />

      {unreadCount > 0 && (
        <span
          aria-hidden="true"
          className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-[3px] flex items-center justify-center bg-red-600 text-white text-[10px] font-bold rounded-full tabular-nums"
        >
          {badgeLabel(unreadCount)}
        </span>
      )}
    </button>
  );
}
